import { formatDateOnly } from "./formatDateOnly";

/**
 * Display-timezone seam (read/display increment, piece c).
 *
 * TWO structurally-separate entry points so the date-only bug class is impossible
 * by construction, in BOTH directions:
 *   - formatDateOnly(plainDate): re-exported from formatDateOnly.ts. Takes NO timezone
 *     and never `new Date(string)` internally — a date physically cannot reach instant
 *     machinery.
 *   - formatInstant(iso, tz): renders a UTC instant in an explicit IANA zone. It REJECTS
 *     a date-only value (a bare "YYYY-MM-DD") rather than silently best-guessing midnight
 *     — so an instant formatter cannot quietly eat a date.
 *
 * The display tz comes from resolveDisplayTz (the TOTAL chain). NEVER the browser zone,
 * NEVER the server zone.
 */
export { formatDateOnly };

/** Read-time last-resort display floor (Pacific/Portland). A CODE CONSTANT. */
export const FLOOR_DISPLAY_TZ = "America/Los_Angeles";

/**
 * Resolve the IANA zone to RENDER a time in. TOTAL — never throws, never returns the
 * browser/server zone. The chain is: an explicitly-passed community zone, else the user's
 * personal display tz, else the Pacific floor. (The instance/digest tz is deliberately NOT in
 * the display chain — an unset user floors straight to Pacific, not email_digest_config.)
 *
 * `timezone_mode` is LIVE, not dormant. What was once removed here was per-RECORD community-zone
 * display (each row in its own community's zone); it is not coming back — the #921 ruling
 * (2026-08-17) rejected it outright because mixing zones inside one list forces per-row zone
 * labels. What exists instead is a per-VIEWER mode over a single active community: useCommunityTz
 * reads `user_preferences.timezone_mode` and, in `community` mode with one community selected in
 * the top bar, hands its zone to the `communityTz` parameter below. See useCommunityTz.ts.
 */
export function resolveDisplayTz(
  userTz: string | null | undefined,
  communityTz?: string | null,
): string {
  // Global community filter (OPT-IN): when a tz-YES surface passes a single selected community's
  // zone, render in it. Same IANA probe as the personal path. Absent/All (undefined/null) or an
  // invalid community zone falls THROUGH to the personal chain below, which is byte-identical to
  // the no-arg behavior - so tz-NO surfaces (no-arg callers) are untouched by construction.
  if (communityTz) {
    try {
      new Intl.DateTimeFormat(undefined, { timeZone: communityTz });
      return communityTz;
    } catch {
      /* invalid community zone -> fall through to the personal chain */
    }
  }
  if (!userTz) return FLOOR_DISPLAY_TZ;
  // Defense-in-depth: a corrupt/legacy stored zone must never reach toLocaleString and throw a
  // RangeError that kills the render. Probe with the REAL IANA validator (not a curated list, so a
  // valid-but-uncurated zone like Europe/Berlin is honored); floor only an ACTUALLY-invalid value.
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: userTz });
    return userTz;
  } catch {
    return FLOOR_DISPLAY_TZ;
  }
}

/**
 * Render a UTC INSTANT (timestamptz ISO with Z/offset) in an explicit IANA zone.
 * Rejects a date-only value (use formatDateOnly for those). Returns "—" for null/invalid.
 * `opts` are Intl.DateTimeFormat options; default = "MMM d, yyyy, h:mm a"-style. Pass
 * `{ hour: undefined, minute: undefined }`-free overrides or `showTime: false` to subsume
 * dateFormatter.ts's showTime=false callers (an instant rendered as a DATE in the zone —
 * still an instant, still tz-correct, NOT a date-only value).
 */
export function formatInstant(
  iso: string | null | undefined,
  tz: string,
  opts?: { showTime?: boolean; locale?: string } & Intl.DateTimeFormatOptions,
): string {
  if (iso == null) return "—";
  const s = String(iso).trim();
  if (!s) return "—";
  // A bare date-only value must NOT be silently midnight-guessed into an instant.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    throw new Error(`formatInstant received a date-only value "${s}" — use formatDateOnly()`);
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return "—";
  const { showTime = true, locale = "en-US", ...rest } = opts ?? {};
  return getFormatter(locale, {
    timeZone: tz,
    year: "numeric",
    month: "short",
    day: "numeric",
    ...(showTime ? { hour: "numeric", minute: "2-digit" } : {}),
    ...rest,
  }).format(d);
}

/**
 * Intl.DateTimeFormat cache. Constructing a formatter is the expensive part of toLocaleString;
 * on large lists (hundreds of rows) the SAME (locale, timeZone, options) recurs every row/render.
 * The cache key is locale + the FULL options object (timeZone AND every field), so distinct-option
 * callsites never collide. `.format(d)` is byte-identical to `d.toLocaleString(locale, opts)`.
 */
const _fmtCache = new Map<string, Intl.DateTimeFormat>();
function getFormatter(locale: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = locale + " " + JSON.stringify(opts);
  let f = _fmtCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, opts);
    _fmtCache.set(key, f);
  }
  return f;
}

/**
 * Render ONLY the time-of-day of a UTC instant in an explicit tz (e.g. "2:00 PM").
 * Thin convenience over formatInstant for the common "date on one line, time on the
 * next" list/table layout. Same date-only rejection + "—" guards.
 */
export function formatInstantTime(
  iso: string | null | undefined,
  tz: string,
  locale = "en-US",
): string {
  if (iso == null) return "—";
  const s = String(iso).trim();
  if (!s) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    throw new Error(`formatInstantTime received a date-only value "${s}" — use formatDateOnly()`);
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return "—";
  return getFormatter(locale, { timeZone: tz, hour: "numeric", minute: "2-digit" }).format(d);
}
